import {
  Box,
  Button,
  Flex,
  Modal,
  SortableTable,
  SubmitButton,
  Text,
} from '@/components';
import { useSortOrder } from '@/hooks';
import { useMessage } from '@/providers';
import {
  TABLE_CODE_WIDTH,
  TABLE_DATETIME_WIDTH,
  TABLE_YEAR_WIDTH,
  datetimeFormat,
} from '@/utils';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useChangeBudgetAgendaOrderOfExpenditurePurposes,
  useChangeBudgetAgendaOrderOfRevenueNatures,
} from '../hooks';
import { ExpenditurePurposeLevelLabelModifier } from '../models';
import { fillZeroToAccountType } from '../utils';

type BudgetAgendaSortOrderDataModelElement = {
  id: string;
  code: number;
  name: string;
  level: number;
  startYear: number | null;
  endYear: number | null;
  budgetAgendaSortOrder: number | null;
  createdAt: string;
  updatedAt: string;
};
export type BudgetAgendaSortOrderDataModel =
  BudgetAgendaSortOrderDataModelElement[];

type Props = {
  type: 'expenditure-purpose' | 'revenue-nature';
  open: boolean;
  onCancel: () => void;
  dataSource: BudgetAgendaSortOrderDataModel;
};

export const BudgetAgendaSortOrderModal: FC<Props> = ({
  type,
  open,
  onCancel,
  dataSource,
}) => {
  const { t } = useTranslation();
  const { sortedData, onDragEnd, hasDataIndexChanged } =
    useSortOrder<BudgetAgendaSortOrderDataModelElement>(dataSource);
  const { openMessage } = useMessage();
  const { mutateAsync: updateExpenditurePurposesSortOrderAsync } =
    useChangeBudgetAgendaOrderOfExpenditurePurposes();
  const { mutateAsync: updateRevenueNatureSortOrderAsync } =
    useChangeBudgetAgendaOrderOfRevenueNatures();

  const columns = [
    {
      title: t('code'),
      key: 'code',
      dataIndex: 'code',
      width: TABLE_CODE_WIDTH,
      render: fillZeroToAccountType,
    },
    {
      title: t('level'),
      key: 'level',
      dataIndex: 'level',
      width: TABLE_CODE_WIDTH,
      render: (value) => ExpenditurePurposeLevelLabelModifier[value],
    },
    {
      title: t('name'),
      key: 'name',
      dataIndex: 'name',
    },
    {
      title: t('start-fiscal-year'),
      key: 'startYear',
      dataIndex: 'startYear',
      width: TABLE_YEAR_WIDTH,
      onCell: () => ({
        style: { minWidth: TABLE_YEAR_WIDTH },
      }),
    },
    {
      title: t('end-fiscal-year'),
      key: 'endYear',
      dataIndex: 'endYear',
      width: TABLE_YEAR_WIDTH,
      onCell: () => ({
        style: { minWidth: TABLE_YEAR_WIDTH },
      }),
    },
    {
      title: t('created-datetime'),
      key: 'createdAt',
      dataIndex: 'createdAt',
      width: TABLE_DATETIME_WIDTH,
      render: (value) => datetimeFormat({ datetime: value }),
    },
    {
      title: t('updated-datetime'),
      key: 'updatedAt',
      dataIndex: 'updatedAt',
      width: TABLE_DATETIME_WIDTH,
      render: (value) => datetimeFormat({ datetime: value }),
    },
  ];

  const onSubmit = async () => {
    const params = sortedData.map((d, i) => ({
      id: d.id,
      budgetAgendaSortOrder: i + 1,
    }));

    if (type === 'expenditure-purpose') {
      await updateExpenditurePurposesSortOrderAsync(params);
    } else {
      await updateRevenueNatureSortOrderAsync(params);
    }
    await Promise.all([
      openMessage('success', t('message-update-completed')),
      (async () => {
        onCancel();
      })(),
    ]);
  };

  return (
    <Modal
      title={<Text text={t('budget-agenda-sort-order-edit')} variant="h5" />}
      open={open}
      onCancel={onCancel}
      footer={
        <Flex gap={4} justify="flex-end">
          <Button onClick={onCancel}>{t('cancel')}</Button>
          <SubmitButton onClick={onSubmit} disabled={!hasDataIndexChanged} />
        </Flex>
      }
      width={1000}
    >
      <Box marginTop={24} marginBottom={24} maxHeight={500} overflowY="scroll">
        <SortableTable<BudgetAgendaSortOrderDataModel>
          dataSource={sortedData}
          columns={columns}
          rowKey="id"
          rowKeys={sortedData.map((d) => d.id)}
          onDragEnd={onDragEnd}
        />
      </Box>
    </Modal>
  );
};
